import moment from 'moment-timezone';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { DailySubscriptionStatus } from './daily-subscription-status';
import { DepositBankAccount } from './deposit-bank-account';
import { OrganizationProfile } from './organization-profile';
import { OrganizationRates } from './organization-rates';
import { PaymentAttempt } from './payment-attempt';
import { Subscription } from './subscription';
import { UnifiedBankAccount } from './unified-bank-account';
import { PaymentGroup } from './payment-group';
import { CustomUnitField } from './custom-unit-field';
import { OrganizationPreference } from 'app/models/entities/organization-preference';
import { CreditCard } from './credit-card';
import { PaymentBankAccount } from './payment-bank-account';
import { AccountCredit } from './account-credit';
import { PaginationMeta } from './pagination';
import { Membership } from './membership';
import { BaseAvatar } from './base/avatar';
import { FeatureFlag } from './feature-flag';
import { AssignedSalesRep } from './assigned-sales-rep';
import environment from '../../../environments/environment';
import Moment = moment.Moment;

export class Organization extends BaseAvatar
{
    id: number;
    siteId: number;
    parentOrganizationId: number;
    budgetTemplateId: number;
    verified: boolean;
    createdInProduction: boolean;
    createdAt: Moment;
    updatedAt: Moment;
    deletedAt: Moment;

    customUnitFields?: CustomUnitField[];

    billingSubscription: Subscription;
    isRollupGroup: boolean;
    fundableAmount: number;
    stripeDepositLegalEntities: any[];
    hasBillingMethod: boolean;
    pastDueBalance: number;
    propertyManagementCompanyId: number;
    propertyManagementCompanyName: string;
    openSubmissionsCount: number;
    openViolationsCount: number;
    unitsCount: number;

    // TODO: implement counts for pmc details view
    ownersCount: number;
    vendorsCount: number;

    profile: OrganizationProfile;
    rates: OrganizationRates;
    unifiedBankAccounts: UnifiedBankAccount[];
    depositBankAccounts: DepositBankAccount[];
    paymentBankAccounts: PaymentBankAccount[];
    dailySubscriptionStatuses: DailySubscriptionStatus[];
    subscriptionPayments: PaymentGroup[];
    subscriptions: Subscription[];
    unassociatedFunds: PaymentAttempt[];
    site: any;
    children: Organization[];
    preferences: OrganizationPreference;
    creditCards: CreditCard[];
    accountCredits: AccountCredit[] = [];
    admins: Membership[];
    featureFlags: FeatureFlag[] = [];

    assignedSalesRep: AssignedSalesRep;
    assignedSalesRepId: number;
    demo: boolean;
    trialExpires: number;
    hasPaymentMethod: boolean;

    constructor(organization: any) {
        super(organization);

        if (organization.id) {
            this.id = organization.id;
        }
        if (organization.siteId) {
            this.siteId = organization.siteId;
        }
        if (organization.parentOrganizationId) {
            this.parentOrganizationId = organization.parentOrganizationId;
        }
        if (organization.budgetTemplateId) {
            this.budgetTemplateId = organization.budgetTemplateId;
        }
        if (organization.verified) {
            this.verified = organization.verified;
        }
        if(environment.production) {
            this.createdInProduction = true;
        } else {
            this.createdInProduction = organization.createdInProduction || false;
        }

        if (organization.site) {
            this.site = organization.site;
        }

        if (organization.children) {
            this.children = organization.children;
        }

        if (organization.billingSubscription) {
            this.billingSubscription = new Subscription(organization.billingSubscription);
        }
        if (organization.isRollupGroup) {
            this.isRollupGroup = organization.isRollupGroup;
        }
        if (organization.fundableAmount) {
            this.fundableAmount = organization.fundableAmount;
        }
        if (organization.stripeDepositLegalEntities) {
            this.stripeDepositLegalEntities = organization.stripeDepositLegalEntities;
        }
        if (organization.hasBillingMethod) {
            this.hasBillingMethod = organization.hasBillingMethod;
        }
        if (organization.propertyManagementCompanyId) {
            this.propertyManagementCompanyId = organization.propertyManagementCompanyId;
        }
        if (organization.propertyManagementCompanyName) {
            this.propertyManagementCompanyName = organization.propertyManagementCompanyName;
        }
        this.trialExpires = organization.trialExpires || null;
        this.hasPaymentMethod = organization.hasPaymentMethod || false;

        const timezone = LegFiJwtService.getTimezone();
        if (organization.createdAt) {
            this.createdAt = moment.utc(organization.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (organization.updatedAt) {
            this.updatedAt = moment.utc(organization.updatedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (organization.deletedAt) {
            this.deletedAt = moment.utc(organization.deletedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (organization.customUnitFields) {
            this.customUnitFields = organization.customUnitFields.map((customUnitField) => {
                return new CustomUnitField(customUnitField);
            });
        }

        if (organization.accountCredits) {
            this.accountCredits = organization.accountCredits.map((accountCredit) => {
                return new AccountCredit(accountCredit);
            });
        }

        if (organization.profile) {
            this.profile = new OrganizationProfile(organization.profile);
        }
        if (organization.rates) {
            this.rates = new OrganizationRates(organization.rates);
        }
        if (organization.unifiedBankAccounts) {
            this.unifiedBankAccounts = organization.unifiedBankAccounts.map(
                    (bankAccount) => {
                        return new UnifiedBankAccount(bankAccount);
                    },
            );
        }
        if (organization.depositBankAccounts) {
            this.depositBankAccounts = organization.depositBankAccounts.map(
                    (depositBankAccount) => {
                        return new DepositBankAccount(depositBankAccount);
                    },
            );
        }
        if (organization.dailySubscriptionStatuses) {
            this.dailySubscriptionStatuses = organization.dailySubscriptionStatuses.map(
                    (dailySubscriptionStatus) => {
                        return new DailySubscriptionStatus(dailySubscriptionStatus);
                    },
            );
        }
        if (organization.subscriptionPayments) {
            this.subscriptionPayments = organization.subscriptionPayments.map(
                    (payment) => {
                        return new PaymentGroup(payment);
                    },
            );
        }
        if (organization.subscriptions) {
            this.subscriptions = organization.subscriptions.map(
                    (subscription) => {
                        return new Subscription(subscription);
                    },
            );
        }
        if (organization.unassociatedFunds) {
            this.unassociatedFunds = organization.unassociatedFunds.map(
                    (payment) => {
                        return new PaymentAttempt(payment);
                    },
            );
        }
        if (organization.preferences) {
            this.preferences = new OrganizationPreference(organization.preferences);
        }
        if (organization.hasOwnProperty('pastDueBalance')) {
            this.pastDueBalance = organization.pastDueBalance;
        }
        if (organization.creditCards) {
            this.creditCards = organization.creditCards.map(cc => new CreditCard(cc));
        }
        if (organization.paymentBankAccounts) {
            this.paymentBankAccounts = organization.paymentBankAccounts.map(cc => new PaymentBankAccount(cc));
        }
        if (organization.openSubmissionsCount) {
            this.openSubmissionsCount = organization.openSubmissionsCount;
        }
        if (organization.openViolationsCount) {
            this.openViolationsCount = organization.openViolationsCount;
        }
        if (organization.unitsCount) {
            this.unitsCount = organization.unitsCount;
        }
        if (organization.admins) {
            this.admins = organization.admins.map((a) => new Membership(a));
        }
        if (organization.featureFlags) {
            this.featureFlags = organization.featureFlags.map(ff => new FeatureFlag(ff));
        }
        if (organization.assignedSalesRep) {
            this.assignedSalesRep = new AssignedSalesRep(organization.assignedSalesRep);
        }
        if (organization.assignedSalesRepId) {
            this.assignedSalesRepId = organization.assignedSalesRepId;
        }
        if (organization.demo) {
            this.demo = organization.demo;
        }
    }

    get creditSum() {
        return this.accountCredits.reduce((total, credit) => {
            return total + credit.remaining;
        }, 0);
    }
}

export class OrganizationResponse
{
    data: Organization[];
    meta: PaginationMeta;

    constructor(request: any) {
        this.data = (request.data && request.data.length) ? request.data.map((c: any) => {
            return new Organization(c);
        }) : [];
        this.meta = new PaginationMeta(request);
    }
}

